import { Form, UploadFile } from "antd";
import { useState, useEffect } from "react";
import Papa from "papaparse";
import axiosInstance from "../utils/axiosInstance";
import { GeoLiftJobConfigService } from "../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import { getTenantId } from "../utils/auth";

interface FormValues {
  testMarket: string;
  postTestData: UploadFile[];
  syntheticMarket: string;
}

interface AgFormValues {
  testMarket: string;
  postTestData: UploadFile[];
  startDuration: string;
  endDuration: string;
  adSpendColumn?: any;
  adsSpent?: any;
}

interface PostTestData {
  success: boolean;
  message: string;
  data: Record<string, any>;
}

export const useGeoLiftForm = (
  jobConfigId: string,
  setPostTestData?: React.Dispatch<React.SetStateAction<PostTestData>>,
  onNext?: () => void
) => {
  const [form] = Form.useForm<FormValues>();
  const [agform] = Form.useForm<AgFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvOutput, setCsvOutput] = useState<any | null>(null);
  const [csvData, setCsvData] = useState<any>([]);
  const [testMarkets, setTestMarkets] = useState<string[]>([]);
  const [columns, setColumns] = useState<
    { title: string; dataIndex: string }[]
  >([]);
  const [syntheticLocationMap, setSyntheticLocationMap] = useState<
    Record<string, string[]>
  >({});
  const [syntheticMarketOptions, setSyntheticMarketOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [showSyntheticField, setShowSyntheticField] = useState(false);
  const geoLiftJobConfigService = new GeoLiftJobConfigService();

  const getMarketDetails = async () => {
    if (!jobConfigId) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/geo-lift/output/${jobConfigId}`
      );

      // Handle 204 No Content - processing not complete yet
      if (response.status === 204) {
        console.info("Processing not complete yet for job:", jobConfigId);
        return;
      }

      let outputJson = response.data.result;
      if (!outputJson) {
        console.warn("No result data in response");
        return;
      }
      setCsvOutput(outputJson);
      // Extract test markets
      const locations = Object.keys(outputJson)
        .filter((key) => key.includes("/BestMarkets"))
        .map((key) => key.split("/")[0]);
      locations.push("Aggregated Locations");
      // Update test markets state
      setTestMarkets(locations);

      // Extract and process synthetic markets
      const syntheticMap: Record<string, string[]> = {};
      Object.keys(outputJson).forEach((key) => {
        if (key.includes("/BestMarkets")) {
          const marketName = key.split("/")[0];
          const syntheticLocations = outputJson[key].map((market: any) =>
            market.location
              .replaceAll("(", "")
              .replace(")", "")
              .replaceAll("'", "")
          );
          syntheticMap[marketName] = syntheticLocations;
        }
      });

      // Update synthetic location map
      setSyntheticLocationMap(syntheticMap);

      // If we have locations, set the first one as default and update synthetic options
      if (locations.length > 0) {
        const firstMarket = locations[0];
        form.setFieldsValue({ testMarket: firstMarket });
        handleTestMarketChange(firstMarket);
      }
    } catch (error) {
      console.error("Error fetching market details:", error);
      // Set default values
      setTestMarkets(["Aggregated Locations"]);
      setSyntheticLocationMap({});
    } finally {
      setLoading(false);
    }
  };

  // Call getMarketDetails when component mounts or jobConfigId changes
  useEffect(() => {
    if (jobConfigId) {
      getMarketDetails();
    }
  }, [jobConfigId]);

  const onFinish = async (values: FormValues | AgFormValues) => {
    setLoading(true);

    try {
      const formData = new FormData();

      // If we have a new file, use it. Otherwise, use the existing file from S3
      if (values.postTestData && values.postTestData.length > 0) {
        const file = values.postTestData[0].originFileObj as File;
        formData.append("csv_file", file);
      } else {
        // Get the existing file from S3
        const fileResponse = await geoLiftJobConfigService.getFile(
          `${jobConfigId}/input/geo_lift_post_data.csv`
        );
        if (!fileResponse) {
          throw new Error("No post-test data file found in S3");
        }
        const blob = new Blob([fileResponse], { type: "text/csv" });
        const file = new File([blob], "geo_lift_post_data.csv", {
          type: "text/csv",
        });
        setCsvData(file);
        formData.append("csv_file", file);
      }

      if (values.testMarket === "Aggregated Locations") {
        let totalAdSpend = 0;
        const adSpendColumn = "adspend";
        // Parse the file to get total ad spend
        const file = formData.get("csv_file") as File;
        await new Promise<void>((resolve, reject) => {
          Papa.parse(file, {
            complete: (results: any) => {
              if (!results.meta.fields?.includes(adSpendColumn)) {
                console.error(`Column "${adSpendColumn}" not found in CSV.`);
                reject(
                  new Error(
                    `Ad Spend Column "${adSpendColumn}" not found in CSV.`
                  )
                );
                return;
              }

              totalAdSpend = results.data.reduce((sum: number, row: any) => {
                const value = parseFloat(row[adSpendColumn]);
                return isNaN(value) ? sum : sum + value;
              }, 0);

              resolve();
            },
            error: (error) => {
              console.error("Error parsing CSV:", error);
              reject(error);
            },
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true,
          });
        });

        formData.append("jobConfigId", jobConfigId);
        formData.append("tenantId", getTenantId());
        formData.append("confidenceThreshold", "80");
        formData.append("startDuration", "97");
        formData.append("endDuration", "105");
        formData.append("adSpendColumn", "Ad Spend");
        formData.append("totalAdSpend", totalAdSpend.toString());
        formData.append("adsSpent", "0");

        const response = await axiosInstance.post(
          "/model/geo_lift/aggregate_lift_percentage",
          formData
        );

        if (setPostTestData) {
          setPostTestData(response.data);
          onNext?.();
        }
      } else {
        const inValues = values as FormValues;
        console.log(inValues,"inValues");
        formData.append("marketSelectionId", "0");
        formData.append("startDuration", "97");
        formData.append("endDuration", "105");
        formData.append("marketsList", inValues.syntheticMarket);
        formData.append("jobConfigId", jobConfigId);
        formData.append("market_name", inValues.testMarket);
        formData.append("adSpendColumn", "adspend");
        formData.append("calculatedAdSpend", "153636.30552602257");
        const response = await axiosInstance.post(
          "/model/geo_lift/post_test",
          formData
        );
        console.log("API response:", response.data);
        if (setPostTestData) {
          setPostTestData(response.data);
          onNext?.();
        }
      }
    } catch (error) {
      console.error("Error processing analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestMarketChange = (value: string) => {
    if (value === "Aggregated Locations") {
      setShowSyntheticField(false);
      setSyntheticMarketOptions([]);
    } else {
      setShowSyntheticField(true);
      const syntheticLocations = syntheticLocationMap[value] || [];
      const options = syntheticLocations.slice(0, 1).map((location) => ({
        value: location,
        label: location,
      }));
      setSyntheticMarketOptions(options);

      // Update form with the synthetic market
      if (options.length > 0) {
        form.setFieldsValue({
          syntheticMarket: options[0].label,
        });
      }
    }
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      return false;
    },
    onChange: async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
      try {
        const latestFile = newFileList[newFileList.length - 1]?.originFileObj;
        if (latestFile) {
          await geoLiftJobConfigService.uploadFile(
            latestFile,
            `${jobConfigId}/input/geo_lift_post_data.csv`
          );
        }
      } catch (error) {
        console.error("File upload failed:", error);
      }
      setFileList(newFileList);
      form.setFieldsValue({ postTestData: newFileList });
    },
    fileList,
    accept: ".csv",
    onRemove: () => {
      setCsvData([]);
      setFileList([]);
      form.setFieldsValue({ postTestData: [] });
    },
  };

  useEffect(() => {
    if (syntheticMarketOptions?.length > 0) {
      form.setFieldsValue({
        syntheticMarket: syntheticMarketOptions[0].label,
      });
    }
  }, [syntheticMarketOptions]);

  return {
    form,
    agform,
    fileList,
    loading,
    csvData,
    testMarkets,
    columns,
    syntheticMarketOptions,
    showSyntheticField,
    uploadProps,
    onFinish,
    handleTestMarketChange,
    setCsvData,
    setFileList,
    csvOutput,
  };
};
